/**
 * What PostHog may see of the screen and the address bar. Pure, so the rules
 * are unit-tested (tests/analytics-privacy.test.ts); lib/analytics.ts wires
 * them into posthog-js.
 *
 * The log is a family's care record: babies' names, feeds, medicines, notes.
 * Session replays therefore mask every word and every input, and the
 * attributes that repeat them (an aria-label names the medicine), keeping only
 * the page's shape, taps, and scrolling. Text inside an element marked
 * `data-ph-unmask` (the landing and legal pages, which show no household
 * data) stays readable.
 */

/** Query parameters that are credentials or one-time OAuth values. */
export const SENSITIVE_PARAMS = [
  "code",
  "state",
  "sig",
  "code_challenge",
  "token",
  "id_token",
  "access_token",
  "refresh_token",
  "client_secret",
] as const;

const SENSITIVE = new Set<string>(SENSITIVE_PARAMS);
const MASK = "<MASKED>";

/** A query key as a URL parser reads it (`%73ig` is `sig`); malformed escapes stay as written. */
function decodedKey(key: string): string {
  try {
    return decodeURIComponent(key.replace(/\+/g, " ")).toLowerCase();
  } catch {
    return key.toLowerCase();
  }
}

/** `/consent?client_id=x&sig=abc` → `/consent?client_id=x&sig=<MASKED>`; other URLs unchanged. */
export function redactUrl(url: string): string {
  const q = url.indexOf("?");
  if (q === -1) return url;
  const hash = url.indexOf("#", q);
  const end = hash === -1 ? url.length : hash;
  let changed = false;
  const pairs = url
    .slice(q + 1, end)
    .split("&")
    .map((pair) => {
      const eq = pair.indexOf("=");
      const key = eq === -1 ? pair : pair.slice(0, eq);
      if (!SENSITIVE.has(decodedKey(key))) return pair;
      changed = true;
      return `${key}=${MASK}`;
    });
  return changed ? `${url.slice(0, q + 1)}${pairs.join("&")}${url.slice(end)}` : url;
}

/** Every string property that holds a URL, redacted (a defensive pass over a whole event). */
export function redactUrlProperties<T extends Record<string, unknown>>(properties: T): T {
  const out: Record<string, unknown> = { ...properties };
  for (const [key, value] of Object.entries(out)) {
    if (typeof value === "string" && value.includes("?")) out[key] = redactUrl(value);
  }
  return out as T;
}

/** Replay text: masked, keeping its length (layout), unless it sits in an unmasked region. */
export function maskReplayText(text: string, element?: Element | null): string {
  if (element?.closest("[data-ph-unmask]")) return text;
  return text.replace(/\S/g, "*");
}

/** Attributes that carry words a person reads, and can repeat household data. */
const WORDY_ATTRIBUTES = new Set(["alt", "placeholder", "title", "value"]);

/** Replay attributes: wordy ones masked (outside unmasked regions); layout and styling kept. */
export function maskReplayAttribute(name: string, value: string, element?: Element | null): string {
  const wordy = WORDY_ATTRIBUTES.has(name) || name.startsWith("aria-");
  if (!wordy || element?.closest("[data-ph-unmask]")) return value;
  return value.replace(/\S/g, "*");
}
